/**
 * EAA-1644 WP5: resolve where a SCORM package is served from before the launch HTML is
 * fetched. The backend's access endpoint answers the package base URL — the server serve
 * path (`/api/content/scorm/{id}/files/`, delivery mode `server`) or an absolute CloudFront
 * URL (mode `cdn`, with signed cookies set on the response). The stored
 * `Content.data.packageUrl` is never rewritten (plan D7), so it stays the fallback.
 *
 * See emg-ai-content-hub-be docs/plans/storage/PLAN-Storage-Direct-S3-EAA-1644.md §2 WP4/WP5.
 */

import { useEffect, useRef, useState } from 'react';

import { getSharedApiAuthHeaders } from '@ai-content-hub/shared-api';

import { requestScormAccess, ScormAccessError, type ScormAccessErrorKind } from './api';

/** Access failures that keep today's behaviour (stored package URL) instead of failing the player. */
const FALLBACK_KINDS: ReadonlySet<ScormAccessErrorKind> = new Set<ScormAccessErrorKind>([
    'not_found',
    'unauthorized',
    'forbidden',
    'network',
]);

/** Refresh the CDN cookies this long before they expire. */
const REFRESH_LEAD_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 30_000;
const MAX_TIMER_MS = 2_147_483_647;

export interface ScormAccessTarget {
    /** Today's package base URL, already made absolute the way the caller always did. */
    storedBaseUrl: string;
    /** Saved package id, or null for preview uploads / unknown ids. */
    packageId: string | null;
    /** Content-session id (embed / learner) — selects the session variant. */
    sessionId?: string | null;
    /** Session JWT for the session variant; when absent the shared API headers are used. */
    sessionToken?: string | null;
    /** API base including `/api`; defaults to the shared API config. */
    apiBaseUrl?: string;
    /** Makes a relative `package_base_url` absolute, matching the caller's existing rule. */
    toAbsolute: (url: string) => string;
}

export interface ResolvedScormBase {
    /** Base URL for the launch document and `<base href>`. */
    baseUrl: string;
    /** True when the base is a CDN URL whose requests need the signed cookies. */
    credentialed: boolean;
    /** Cookie expiry (ISO) when access was granted by the CDN; null otherwise. */
    expiresAt: string | null;
}

function isAbsoluteUrl(url: string): boolean {
    return /^https?:\/\//i.test(url) || url.startsWith('//');
}

function accessHeaders(target: ScormAccessTarget): Record<string, string> {
    if (target.sessionId && target.sessionToken) {
        return { Authorization: `Session ${target.sessionToken}` };
    }
    return getSharedApiAuthHeaders();
}

function requestFor(target: ScormAccessTarget) {
    return requestScormAccess({
        packageId: target.packageId,
        sessionId: target.sessionId,
        apiBaseUrl: target.apiBaseUrl,
        authHeaders: accessHeaders(target),
    });
}

/**
 * Ask for access and resolve the base. Falls back to the stored URL when there is nothing
 * to ask about (preview upload) or the caller cannot use the endpoint (404/401/403/network),
 * which is exactly today's server-mode behaviour. A 503 (CDN misconfigured) is thrown.
 */
export async function resolveScormLaunchBase(target: ScormAccessTarget): Promise<ResolvedScormBase> {
    const stored: ResolvedScormBase = { baseUrl: target.storedBaseUrl, credentialed: false, expiresAt: null };
    if (!target.sessionId && !target.packageId) {
        return stored;
    }
    try {
        const grant = await requestFor(target);
        if (isAbsoluteUrl(grant.packageBaseUrl)) {
            return { baseUrl: grant.packageBaseUrl, credentialed: true, expiresAt: grant.expiresAt };
        }
        return { baseUrl: target.toAbsolute(grant.packageBaseUrl), credentialed: false, expiresAt: null };
    } catch (err) {
        if (err instanceof ScormAccessError && FALLBACK_KINDS.has(err.kind)) {
            console.warn('[SCORM] access endpoint unavailable, using stored package URL:', err.kind, err.status);
            return stored;
        }
        throw err;
    }
}

/** Delay before re-requesting access so the cookies never lapse mid-session. */
export function accessRefreshDelayMs(expiresAt: string, now: number = Date.now()): number | null {
    const expiry = Date.parse(expiresAt);
    if (Number.isNaN(expiry)) return null;
    const delay = Math.max(MIN_REFRESH_DELAY_MS, expiry - now - REFRESH_LEAD_MS);
    return Math.min(delay, MAX_TIMER_MS);
}

/** Fetch the launch HTML; CDN bases send the signed cookies, server bases behave as before. */
export async function fetchScormLaunchHtml(url: string, credentialed: boolean): Promise<string> {
    const resp = credentialed ? await fetch(url, { credentials: 'include' }) : await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.text();
}

export interface ScormAccessState {
    resolved: ResolvedScormBase | null;
    error: string | null;
}

/**
 * Resolve the package base once per target and keep CDN cookies fresh until unmount.
 * `enabled` gates the request (e.g. until the SCORM API is installed). The request is keyed
 * on the ids and URLs only: a rotated session token is read from a ref, so it never reloads
 * a running SCO. `toAbsolute` must be a stable (module-level) function.
 */
export function useScormPackageAccess(target: ScormAccessTarget, enabled: boolean): ScormAccessState {
    const [state, setState] = useState<ScormAccessState>({ resolved: null, error: null });
    const tokenRef = useRef(target.sessionToken);
    tokenRef.current = target.sessionToken;
    const { storedBaseUrl, packageId, sessionId, apiBaseUrl, toAbsolute } = target;

    useEffect(() => {
        if (!enabled || !storedBaseUrl) return undefined;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const current = (): ScormAccessTarget => ({
            storedBaseUrl, packageId, sessionId, apiBaseUrl, toAbsolute, sessionToken: tokenRef.current,
        });

        const scheduleRefresh = (expiresAt: string | null) => {
            if (!expiresAt || cancelled) return;
            const delay = accessRefreshDelayMs(expiresAt);
            if (delay == null) return;
            timer = setTimeout(() => {
                requestFor(current())
                    .then((grant) => scheduleRefresh(grant.expiresAt))
                    .catch((err) => console.warn('[SCORM] access refresh failed:', err));
            }, delay);
        };

        setState({ resolved: null, error: null });
        resolveScormLaunchBase(current())
            .then((resolved) => {
                if (cancelled) return;
                setState({ resolved, error: null });
                scheduleRefresh(resolved.expiresAt);
            })
            .catch((err) => {
                if (cancelled) return;
                setState({ resolved: null, error: err instanceof Error ? err.message : 'SCORM access failed' });
            });

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [enabled, storedBaseUrl, packageId, sessionId, apiBaseUrl, toAbsolute]);

    return state;
}
